import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Menu, X } from "lucide-react";
import { StickerSun, StickerHeart, StickerStar, StickerPalm } from "./Stickers";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/accomplishments", label: "Accomplishments" },
  { to: "/gallery", label: "Gallery" },
  { to: "/media", label: "Media" },
  { to: "/speaking", label: "Book Me" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/shop", label: "Shop" },
];

const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FFF4D6] grain font-body relative">
      {/* Top marquee */}
      <div
        className="bg-[#8ECAE6] border-b-4 border-black py-1 font-pixel text-2xl uppercase tracking-wider"
        data-testid="top-marquee"
      >
        <Marquee gradient={false} speed={50} pauseOnHover>
          <span className="px-6">★ Welcome to the official CrossCanadaJax website ★</span>
          <span className="px-6">7,000 KM across Canada</span>
          <span className="px-6">$100,000+ raised for cancer research</span>
          <span className="px-6">280 KM across PEI in 65 hours, no sleep</span>
          <span className="px-6">16th human to cross Canada on foot</span>
          <span className="px-6">National guest speaker</span>
          <span className="px-6">Father · Adventurer · Storyteller</span>
          <span className="px-6">surf · skate · run · repeat</span>
        </Marquee>
      </div>

      {/* Header / Nav */}
      <header className="border-b-4 border-black bg-[#FFF4D6] relative z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <StickerSun size={44} rotate={-12} />
            <div className="font-display text-3xl sm:text-4xl tracking-wider leading-none">
              CROSS<span className="text-[#F87171]">CANADA</span>JAX
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display tracking-wider text-lg px-3 py-2 border-4 border-black btn-press
                   ${isActive ? "bg-[#F87171] text-white shadow-[4px_4px_0_#000]" : "bg-white text-black shadow-[4px_4px_0_#000] hover:bg-[#F4D58D]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="xl:hidden border-4 border-black bg-[#F4D58D] p-2 shadow-[4px_4px_0_#000] btn-press"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden border-t-4 border-black bg-[#FFF4D6]" data-testid="mobile-menu">
            <nav className="flex flex-col p-4 gap-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display tracking-wider text-2xl px-4 py-3 border-4 border-black
                     ${isActive ? "bg-[#F87171] text-white" : "bg-white text-black"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main>{children}</main>

      <Footer />
    </div>
  );
};

const Footer = () => (
  <footer className="relative mt-24 border-t-4 border-black bg-[#0A0A0A] text-white">
    <div className="hazard h-3 w-full" />
    <div className="max-w-7xl mx-auto px-6 py-12 relative">
      <div className="absolute -top-10 right-6"><StickerHeart size={70} rotate={14} /></div>
      <div className="absolute -top-12 left-6"><StickerStar size={60} rotate={-10} /></div>

      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <div className="font-display text-4xl tracking-wider text-[#F4D58D]">CROSSCANADAJAX</div>
          <p className="font-pixel text-xl mt-3 text-white/80">
            Move the body. Quiet the mind. Feed the soul.<br/>
            Made in Winnipeg, MB, Canada.
          </p>
        </div>

        <div>
          <div className="font-display text-2xl tracking-wider text-[#8ECAE6] mb-3">Find Me Online</div>
          <ul className="font-pixel text-xl space-y-1">
            <li><a className="retro-link" href="https://www.tiktok.com/@crosscanadajax" target="_blank" rel="noreferrer" data-testid="social-tiktok">TikTok » @crosscanadajax</a></li>
            <li><a className="retro-link" href="https://kick.com/crosscanadajax" target="_blank" rel="noreferrer" data-testid="social-kick">Kick » crosscanadajax</a></li>
            <li><a className="retro-link" href="https://www.instagram.com/crosscanadajax" target="_blank" rel="noreferrer" data-testid="social-instagram">Instagram » @crosscanadajax</a></li>
            <li><a className="retro-link" href="https://www.facebook.com/crosscanadajax" target="_blank" rel="noreferrer" data-testid="social-facebook">Facebook » crosscanadajax</a></li>
            <li><a className="retro-link" href="https://www.youtube.com/@crosscanadajax" target="_blank" rel="noreferrer" data-testid="social-youtube">YouTube » @crosscanadajax</a></li>
          </ul>
        </div>

        <div>
          <div className="font-display text-2xl tracking-wider text-[#F87171] mb-3">Get In Touch</div>
          <p className="font-pixel text-xl">
            <a className="retro-link" href="mailto:CrossCanadaJax97@gmail.com" data-testid="footer-email">CrossCanadaJax97@gmail.com</a>
          </p>
          <Link to="/speaking" className="inline-block mt-4 px-4 py-2 bg-[#F4A261] text-white border-4 border-white font-display text-2xl tracking-wider btn-press shadow-[4px_4px_0_#fff]" data-testid="footer-book-me">
            Book Me to Speak →
          </Link>
        </div>
      </div>

      <div className="border-t-2 border-white/20 mt-12 pt-6 font-pixel text-lg flex flex-col md:flex-row gap-3 justify-between text-white/70">
        <div>© {new Date().getFullYear()} Jackson Charron-Okerlund · CrossCanadaJax</div>
        <div>You are visitor # <span className="text-[#F4D58D]">04,206</span> · best viewed at 1024×768</div>
      </div>
    </div>
  </footer>
);

export default Layout;
